import { Responsive } from "responsive"
import { writable } from "svelte/store"

export namespace TooltipController {
	type ContextType = {
		content: string
		position: {
			top: number
			left: number
		}
		pointing: "left" | "top"
		style: string
	}

	const store = writable<ContextType>({
		content: undefined,
		position: undefined,
		pointing: undefined,
		style: undefined,
	})

	export function subscribe(cb: (ctx: ContextType) => void) {
		return store.subscribe(cb)
	}

	export function useTooltip(
		elem: HTMLElement,
		props: {
			content: string
			style?: string
			pointing?: "left" | "top"
		}
	) {
		const clearTooltip = () => {
			store.set({
				content: undefined,
				position: undefined,
				pointing: undefined,
				style: undefined,
			})
			elem.removeEventListener("mouseout", onMouseOut)
		}

		const showTooltip = () => {
			if (Responsive.isMobile) return

			const rect = elem.getBoundingClientRect()
			const position =
				props.pointing === "top"
					? {
							left: rect.left + rect.width / 2,
							top: rect.bottom,
					  }
					: {
							left: rect.right,
							top: rect.bottom - rect.height / 2,
					  }

			store.set({
				content: props.content,
				position,
				pointing: props.pointing ?? "left",
				style: props.style,
			})
		}

		const onMouseOver = () => {
			showTooltip()
			elem.addEventListener("mouseout", onMouseOut)
		}

		const onMouseOut = () => {
			clearTooltip()
		}

		elem.addEventListener("mouseover", onMouseOver)

		// Auto-clear tooltip if element is removed from the DOM
		const mutationObserver = new MutationObserver((mutationList) => {
			for (const mutation of mutationList) {
				for (const node of mutation.removedNodes) {
					if (node === elem) {
						clearTooltip()
					}
				}
			}
		})

		if (elem.parentNode) {
			mutationObserver.observe(elem.parentNode, { childList: true })
		}
	}
}
